import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { cssMap } from '@atlaskit/css';
import DatePicker from '@atlaskit/datetime-picker/date-picker';
import TimePicker from '@atlaskit/datetime-picker/time-picker';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';

import { times } from '../../../../../../../../common/constants';
import { asDateTimeValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { startOfWeekDay } from '../../../../../../../../common/utils';
import {
	useCurrentValue,
	useFieldInvalid,
	useFieldRequired,
	useFormField,
	useFormRendererActions,
} from '../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';

const styles = cssMap({
	container: {
		maxWidth: '320px',
	},
	pickerContainer: {
		width: '50%',
	},
});

export const EditDateTimeInput = ({
	fieldId,
	fieldInputId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useCurrentValue);
	const isInvalid = useFieldInvalid({ fieldId });

	const currentValue = useCurrentValue({ fieldId });
	const formField = useFormField({ fieldId });
	const { updateModifiedValue } = useFormRendererActions();
	const intl = useIntl();
	const isRequired = useFieldRequired({ fieldId });
	const dateTimeCurrentValue = asDateTimeValue(currentValue);

	const onChangeDate = useCallback(
		(value: string) => {
			if (value.length === 0) {
				if (dateTimeCurrentValue?.localTime === undefined) {
					updateModifiedValue(fieldId, null);
				} else {
					updateModifiedValue(fieldId, { ...dateTimeCurrentValue, date: undefined });
				}
			} else {
				updateModifiedValue(fieldId, { ...dateTimeCurrentValue, date: value });
			}
		},
		[dateTimeCurrentValue, fieldId, updateModifiedValue],
	);

	const onChangeLocalTime = useCallback(
		(value: string) => {
			if (value.length === 0) {
				if (dateTimeCurrentValue?.date === undefined) {
					updateModifiedValue(fieldId, null);
				} else {
					updateModifiedValue(fieldId, { ...dateTimeCurrentValue, localTime: undefined });
				}
			} else {
				updateModifiedValue(fieldId, { ...dateTimeCurrentValue, localTime: value });
			}
		},
		[dateTimeCurrentValue, fieldId, updateModifiedValue],
	);

	// NOTE: We are using the individual `DatePicker` and `TimePicker` because there is a problem
	//       when using the `DateTimePicker` and `onChange` updating multiple properties
	//       (e.g. `date` and `localTime`) where the `onChange` for the date picker and time picker
	//       being fired immediately one after the other, not leaving time for the rerender to update
	//       the `currentValue`  which means that the clear button can only clear the time, and users
	//       would have to enter the date picker and press the delete key to clear it. This could be
	//       solved by local state, but that seems rather messy storing the value 3 times.

	return (
		<Inline xcss={styles.container}>
			<Box xcss={styles.pickerContainer}>
				<DatePicker
					id={fieldInputId}
					value={dateTimeCurrentValue?.date ?? ''}
					isInvalid={isInvalid}
					locale={intl.locale}
					onChange={onChangeDate}
					placeholder={intl.formatMessage(messages.datePlaceholder, {
						date: intl.formatDate(new Date(), {}),
					})}
					shouldShowCalendarButton={!dateTimeCurrentValue?.date}
					weekStartDay={startOfWeekDay(intl.locale)}
					testId={`field-input-date-time-date-picker-edit-${fieldId}`}
					isRequired={isRequired}
					label={intl.formatMessage(messages.dateLabel, {
						label: formField?.label,
					})}
					aria-describedby={
						isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
					}
				/>
			</Box>
			<Box xcss={styles.pickerContainer}>
				<TimePicker
					value={dateTimeCurrentValue?.localTime ?? ''}
					isInvalid={isInvalid}
					locale={intl.locale}
					onChange={onChangeLocalTime}
					placeholder={intl.formatMessage(messages.timePlaceholder, {
						time: intl.formatTime(new Date(), {}),
					})}
					testId={`field-input-date-time-time-picker-edit-${fieldId}`}
					times={times}
					isRequired={isRequired}
					timeIsEditable
					label={intl.formatMessage(messages.timeLabel, {
						label: formField?.label,
					})}
					aria-describedby={
						isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
					}
				/>
			</Box>
		</Inline>
	);
};
